import { useContext, useEffect, useState } from "react";
import EventCalendar from "./event-calendar/EventCalendar";
import UiContext from "../../store/ui-context";
import { addDays, subDays, format, set, addMonths, add } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import useAPIRequest from "../../custom_hooks/simple/useAPIRequest";
import useIsMobile from "../../hooks/useIsMobile";
import { setLayoutDashboardTitle } from "../../store/ui-slice";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import ListAppointment from "./list-event/ListAppointment";
import JadwalMobilEditor from "./jadwal-mobil-editor";
import { Calendar } from "primereact/calendar";
import { is } from "date-fns/locale";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "@mui/material";

const shadowTailWind = "shadow-[0px_0px_4px_0px_#00000024]";

const defaultEditorData = {
  id: 0,
  kendaraan_id: null,
  supir_id: null,
  pemakai_id: null,
  tujuan_id: null,
  tanggal_transaksi: null,
  tanggal_perkiraan_berangkat: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
  tanggal_berangkat: null,
  tanggal_pulang: null,
  durasi: 0,
  jumlah_orang: 0,
  status: 0,
  speedo_mulai: 0,
  speedo_akhir: 0,
  harga_bbm: 0,
  keterangan: "",
};

const JadwalMobil = () => {
  const isMobile = useIsMobile();
  const thisMonthFirstDay = isMobile
    ? new Date()
    : set(new Date(), { date: 1 });

  const uiContext = useContext(UiContext);
  const dispatch = useDispatch();
  const permissions = useSelector((state) => state.auth.permissions);

  const [firstLoad, setFirstLoad] = useState(false);

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState([]);

  const [addNewMode, setAddNewMode] = useState(false);
  const [editorData, setEditorData] = useState({});
  const [editorError, setEditorError] = useState({});
  const [editorDialogShowed, setEditorDialogShowed] = useState(false);

  const [editorTitle, setEditorTitle] = useState("New Appointment");
  const [visibleSideBar, setVisibleSideBar] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(thisMonthFirstDay);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingAppointmentId, setEditingAppointmentId] = useState(-1);
  const [editingAppointment, setEditingAppointment] = useState(null);

  const { requestGet, requestGetOne, requestPost, loading } = useAPIRequest();
  const {
    loading: editorLoading,
    requestPost: editorRequestPost,
    requestDelete,
  } = useAPIRequest();

  // use effect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title: "Jadwal Keberangkatan" }));
    handleOnClickChangeMonth(selectedMonth);
  }, []);

  // dummy section

  // API call section

  const getFetchFilter = (date, showAll) => {
    let _filter = "";

    let firstDate;
    if (showAll) {
      firstDate = date;
    } else {
      firstDate = new Date();
      firstDate.setHours(0, 0, 0, 0);
    }
    let lastDayOfMonth = new Date(
      firstDate.getFullYear(),
      firstDate.getMonth() + 1,
      0
    );
    lastDayOfMonth.setHours(23, 59, 59, 999);

    if (isMobile) {
      firstDate = new Date(date.getTime());
      firstDate.setHours(0, 0, 0, 0);

      lastDayOfMonth = new Date(date.getTime());
      lastDayOfMonth.setHours(23, 59, 59, 999);
    }

    _filter = `tanggal_perkiraan_berangkat:>=:${format(
      firstDate,
      "yyyy-MM-dd HH:mm:ss"
    )};tanggal_perkiraan_berangkat:<=:${format(
      lastDayOfMonth,
      "yyyy-MM-dd HH:mm:ss"
    )};data_kendaraan.data_kendaraan_jenis.nama:!=:Trado`;

    return _filter;
  };

  const fetchListDataByMonth = async (date, showAll) => {
    if (loading) return;
    setEvents([]);
    setSelectedMonth(date);

    if (isMobile) {
      showAll = true;
    }

    const _filter = getFetchFilter(date, showAll);

    return requestGet({
      fullUrl: `/api/pemakaian/pemakaianmobil/data`,
      params: {
        page: 1,
        take: 3000,
        filter: _filter,
        order: "tanggal_perkiraan_berangkat",
        order_method: "ASC",
        with_kendaraan: 1,
        with_supir: 1,
        with_pemakai: 1,
      },
      onSuccess: ({ pagination, message, data }) => {
        setEvents(
          data.map((item) => {
            return {
              id: item.id,
              date: new Date(item.tanggal_perkiraan_berangkat),
              title: item.data_kendaraan
                ? item.data_kendaraan.nomor_polisi
                : "-",
              animate: false,
              stale: false,
              data: item,
            };
          })
        );
        if (!firstLoad) {
          setFirstLoad(true);
        }
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const fetchData = async ({ id }) => {
    if (loading) return;
    return requestGetOne({
      fullUrl: `/api/pemakaian/pemakaianmobil/one/${id}`,
      onSuccess: ({ message, data }) => {},
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const deleteData = async ({ ids }) => {
    if (loading) return;

    return requestDelete({
      fullUrl: `/api/pemakaian/pemakaianmobil/delete`,
      ids: ids,
      onSuccess: ({ message, data }) => {
        const newListData = events.filter((el) => !ids.includes(el.id));
        setEvents(newListData);
        setEditorDialogShowed(false);
        uiContext.showSuccessMsg("Success", message);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const saveData = async ({ data }) => {
    resetEditorError();
    if (loading) return;
    const valid = validateData();

    if (valid) {
      const body = convertSaveData(data);

      return editorRequestPost({
        fullUrl: `/api/pemakaian/pemakaianmobil/save`,
        body,
        isForm: false,
        onError: ({ message, data }) => {
          uiContext.showErrorMsg("Error", message);
          setEditorError(data);
        },
        onSuccess: ({ data, message }) => {
          uiContext.showSuccessMsg("Success", message);
          // fetchListData(paging);
        },
      });
    }
    return null;
  };

  // other function
  const validateData = () => {
    let valid = true;
    return valid;
  };

  const convertSaveData = (data) => {
    return data;
  };

  const resetEditorError = () => {
    setEditorError({});
  };

  const getEventsByDate = (date) => {
    return events.filter(
      (event) => format(event.date, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
    );
  };

  // UI event section

  const handleOnClickDate = (date) => {
    if (!visibleSideBar) setVisibleSideBar(true);
    setSelectedDate(date);
  };

  const handleOnClickChangeMonth = (date, showAll) => {
    fetchListDataByMonth(date, showAll);
  };

  const handleOnClickEditData = async (rowData) => {
    setAddNewMode(false);
    resetEditorError();
    const newData = await fetchData({ id: rowData.id });
    if (newData) {
      setEditorData({ ...newData });
      setEditorDialogShowed(true);
    }
  };

  const handleOnClickAddNew = () => {
    setAddNewMode(true);
    resetEditorError();

    setEditorData({
      ...defaultEditorData,
      tanggal_perkiraan_berangkat: format(selectedDate, "yyyy-MM-dd HH:mm:ss"),
    });
    setEditorDialogShowed(true);
  };

  const handleConfirmEditor = async (data) => {
    const newData = await saveData({ data });
    if (newData) {
      if (addNewMode) {
        const _newData = {
          id: newData.id,
          date: new Date(newData.tanggal_perkiraan_berangkat),
          title: newData.data_kendaraan
            ? newData.data_kendaraan.nomor_polisi
            : "-",
          animate: false,
          stale: false,
          data: newData,
        };
        setEvents([_newData, ...events]);
      } else {
        const index = events.findIndex((el) => el.id === newData.id);
        const newListData = [...events];

        const _newData = {
          id: newData.id,
          date: new Date(newData.tanggal_perkiraan_berangkat),
          title: newData.data_kendaraan
            ? newData.data_kendaraan.nomor_polisi
            : "-",
          animate: false,
          stale: false,
          data: newData,
        };
        newListData.splice(index, 1, _newData);
        setEvents(newListData);
      }
      setEditorDialogShowed(false);
    }
  };

  const handleDeleteEditor = (rowData) => {
    resetEditorError();
    const accept = async () => {
      if (loading) return;

      await deleteData({ ids: [rowData.id] });
    };
    const reject = () => {};

    confirmDialog({
      message: "Menghapus jadwal, lanjutkan?",
      header: "Konfirmasi",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept,
      reject,
    });
  };

  const eventTemplate = (rowData) => {
    const {
      date,
      title,
      backgroundColor,
      textColor,
      animate,
      stale,
      index,
      data,
    } = rowData;
    const isPast = date < new Date().setHours(0, 0, 0, 0);

    // console.log("DATA", data);

    return (
      <div
        key={index}
        className={`row-start-start ${
          isPast ? " bg-slate-100 " : shadowTailWind
        } ${stale ? " !bg-slate-50 " : ""} w-full gap-2`}
      >
        <div
          className={`h-full w-[3px] ${
            isPast || stale ? "bg-slate-300" : backgroundColor
          }`}
        ></div>
        <div className={`col-center-start py-1 gap-1`}>
          <div className="col-start-start">
            {data.data_pemakai && (
              <span
                className={`text-xs text-left ${
                  isPast || stale
                    ? "text-slate-400"
                    : "font-semibold text-slate-600"
                }`}
              >
                {data.data_supir.nama}
              </span>
            )}
          </div>
          <div className="col-start-start">
            <span
              className={`text-xs ${
                isPast || stale ? "text-slate-400" : textColor
              } font-semibold text-left`}
            >
              {data.data_kendaraan.jenis_mobil}
            </span>
            <span
              className={`text-xs ${
                isPast || stale ? "text-slate-400" : textColor
              } font-semibold text-left`}
            >
              {data.data_kendaraan.nomor_polisi}
            </span>
          </div>
          <div className="col-start-start">
            {data.data_pemakai &&
              data.data_pemakai.nama !== data.data_supir.nama && (
                <span className="text-xs  text-left text-slate-400">
                  {data.data_pemakai.nama}
                </span>
              )}
            <span className="text-xs md:text-xs text-slate-400">
              {format(date, "hh:mm aa")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`col-start-start p-4 gap-4 w-100`}>
      <ConfirmDialog />

      <JadwalMobilEditor
        title={addNewMode ? "Tambah Jadwal Mobil" : "Edit Jadwal Mobil"}
        data={editorData}
        setData={setEditorData}
        addNewMode={addNewMode}
        loading={editorLoading}
        error={editorError}
        setError={setEditorError}
        visible={editorDialogShowed}
        onHide={() => setEditorDialogShowed(false)}
        onConfirm={handleConfirmEditor}
        onDelete={handleDeleteEditor}
      />

      {isMobile && (
        <div className="elevated-card-tight-vertical !px-4 w-100">
          <Calendar
            className="w-full "
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.value);
              fetchListDataByMonth(e.value, true);
            }}
            inline
            locale="id"
            pt={{
              day: {
                className: "text-sm",
              },
              weekDay: {
                className: "text-sm",
              },
            }}
          />
        </div>
      )}

      {isMobile && (
        <div className="elevated-card-tight-vertical row-between-center !py-2 !px-4 w-100">
          <span className="text-sm font-semibold text-slate-600">
            {selectedDate.toLocaleDateString("id-ID", {
              year: "numeric",
              month: "short",
              day: "numeric",
              weekday: "long",
            })}
          </span>
          <div className="row-start-center gap-2">
            {loading && (
              <ProgressSpinner
                className="h-[1.5rem] w-[1.5rem]"
                strokeWidth="4"
              />
            )}
            <div className="row-start-center gap-1 bg-indigo-500 px-4 py-1 rounded-full">
              <span className="text-sm font-semibold text-white">
                {events.length}
              </span>
              <span className="text-sm font-semibold text-white">Mobil</span>
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <div className="elevated-card-tight-vertical col-start-start !px-4 w-100">
          <ListAppointment
            events={getEventsByDate(selectedDate)}
            onClick={handleOnClickEditData}
          />
        </div>
      )}

      {isMobile && (
        <div className="row-end-end w-full sticky bottom-4 right-0">
          <Button
            className=""
            severity="success"
            icon="pi pi-plus"
            rounded
            raised
            label="Jadwal Baru"
            onClick={handleOnClickAddNew}
          />
        </div>
      )}

      {!isMobile && (
        <Sidebar
          visible={visibleSideBar}
          position="right"
          onHide={() => setVisibleSideBar(false)}
          className="relative"
        >
          <div className="col-start-start gap-2 pt-1">
            <div className="row-start-center gap-1">
              <span className="font-bold">
                {format(selectedDate, "dd MMMM yyyy").toUpperCase()}
              </span>
            </div>
            <ListAppointment
              events={getEventsByDate(selectedDate)}
              onClick={handleOnClickEditData}
            />
            <div className="h-[5rem]" />
            <div className="col-center-center gap-2 right-0 left-0 bottom-4 absolute">
              <Button
                icon="pi pi-plus"
                rounded
                raised
                label="Jadwal Keberangkatan"
                onClick={handleOnClickAddNew}
              />
            </div>
          </div>
        </Sidebar>
      )}

      {!isMobile && (
        <div className="elevated-card-tight-vertical w-100">
          <EventCalendar
            loading={loading}
            events={events}
            selectedMonth={selectedMonth}
            onClick={handleOnClickDate}
            onChangeMonth={handleOnClickChangeMonth}
            onChangeMonthAll={(date) => {
              handleOnClickChangeMonth(date, true);
            }}
            eventTemplate={eventTemplate}
          />
        </div>
      )}
    </div>
  );
};

export default JadwalMobil;
